if (!customElements.get('product-metafield-options')) {
  customElements.define(
    'product-metafield-options',
    class ProductMetafieldOptions extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.product-metafield-options__viewport');
        this.track = this.querySelector('.product-metafield-options__swatches');
        this.links = Array.from(this.querySelectorAll('.product-metafield-options__swatch--link'));
        this.prevButton = this.querySelector('.product-metafield-options__nav--prev');
        this.nextButton = this.querySelector('.product-metafield-options__nav--next');
        this.indicatorThumb = this.querySelector('.product-metafield-options__indicator-thumb');
        this.dragThreshold = 8;
        this.draggingClassTimer = null;
        this.isMouseDown = false;
        this.hasDragged = false;

        if (!this.viewport || !this.track) return;

        this.setConciseLabels();

        this.boundUpdateState = this.updateState.bind(this);
        this.boundMouseMove = this.onMouseMove.bind(this);
        this.boundMouseUp = this.onMouseUp.bind(this);

        this.viewport.addEventListener('scroll', this.boundUpdateState, { passive: true });
        window.addEventListener('resize', this.boundUpdateState);
        this.viewport.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
        this.prevButton?.addEventListener('click', () => this.scrollByAmount(-1));
        this.nextButton?.addEventListener('click', () => this.scrollByAmount(1));

        this.links.forEach((link) => {
          link.addEventListener('click', (event) => {
            if (!this.hasDragged) return;
            event.preventDefault();
          });
        });

        this.updateState();
      }

      setConciseLabels() {
        const swatches = Array.from(this.querySelectorAll('.product-metafield-options__swatch'));
        const fullLabels = swatches.map((swatch) => (swatch.dataset.optionFullLabel || '').trim());
        const wordLists = fullLabels.map((label) => label.split(/\s+/).filter(Boolean));

        if (wordLists.length < 2 || wordLists.some((words) => words.length === 0)) return;

        let sharedPrefix = 0;
        const shortestLength = Math.min(...wordLists.map((words) => words.length));

        while (
          sharedPrefix < shortestLength - 1 &&
          wordLists.every(
            (words) => words[sharedPrefix].toLocaleLowerCase() === wordLists[0][sharedPrefix].toLocaleLowerCase()
          )
        ) {
          sharedPrefix += 1;
        }

        let sharedSuffix = 0;
        while (
          sharedSuffix < shortestLength - sharedPrefix - 1 &&
          wordLists.every(
            (words) =>
              words[words.length - 1 - sharedSuffix].toLocaleLowerCase() ===
              wordLists[0][wordLists[0].length - 1 - sharedSuffix].toLocaleLowerCase()
          )
        ) {
          sharedSuffix += 1;
        }

        swatches.forEach((swatch, index) => {
          const explicitLabel = (swatch.dataset.optionDisplayLabel || '').trim();
          const words = wordLists[index];
          const conciseWords = words.slice(sharedPrefix, sharedSuffix > 0 ? -sharedSuffix : undefined);
          const label = explicitLabel || conciseWords.join(' ') || fullLabels[index];

          swatch.title = fullLabels[index];

          if (swatch.matches('[aria-current="true"]')) {
            const currentValue = this.querySelector('[data-option-current-value]');
            if (currentValue) currentValue.textContent = label;
          }
        });
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.boundUpdateState);
        document.removeEventListener('mousemove', this.boundMouseMove);
        document.removeEventListener('mouseup', this.boundMouseUp);
      }

      updateState() {
        const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
        const canScroll = maxScroll > 4;
        const atStart = this.viewport.scrollLeft <= 4;
        const atEnd = this.viewport.scrollLeft >= maxScroll - 4;

        this.classList.toggle('is-scrollable', canScroll);
        this.classList.toggle('is-at-start', atStart);
        this.classList.toggle('is-at-end', atEnd);
        this.prevButton?.toggleAttribute('disabled', !canScroll || atStart);
        this.nextButton?.toggleAttribute('disabled', !canScroll || atEnd);

        if (this.indicatorThumb) {
          const visibleRatio = canScroll ? this.viewport.clientWidth / this.viewport.scrollWidth : 1;
          const scrollProgress = canScroll ? this.viewport.scrollLeft / maxScroll : 0;
          this.indicatorThumb.style.width = `${visibleRatio * 100}%`;
          this.indicatorThumb.style.left = `${scrollProgress * (1 - visibleRatio) * 100}%`;
        }
      }

      scrollByAmount(direction) {
        this.viewport.scrollBy({
          left: this.viewport.clientWidth * 0.72 * direction,
          behavior: 'smooth',
        });
      }

      onMouseDown(event) {
        if (event.button !== 0) return;
        if (!this.classList.contains('is-scrollable')) return;

        this.isMouseDown = true;
        this.hasDragged = false;
        this.startX = event.clientX;
        this.startScrollLeft = this.viewport.scrollLeft;

        document.addEventListener('mousemove', this.boundMouseMove);
        document.addEventListener('mouseup', this.boundMouseUp);
      }

      onMouseMove(event) {
        if (!this.isMouseDown) return;

        const deltaX = event.clientX - this.startX;
        if (!this.hasDragged && Math.abs(deltaX) > this.dragThreshold) {
          this.hasDragged = true;
          this.viewport.classList.add('is-dragging');
        }

        if (!this.hasDragged) return;

        event.preventDefault();
        this.viewport.scrollLeft = this.startScrollLeft - deltaX;
      }

      onMouseUp() {
        if (!this.isMouseDown) return;

        this.isMouseDown = false;
        document.removeEventListener('mousemove', this.boundMouseMove);
        document.removeEventListener('mouseup', this.boundMouseUp);

        window.clearTimeout(this.draggingClassTimer);
        this.draggingClassTimer = window.setTimeout(() => {
          this.viewport.classList.remove('is-dragging');
          this.hasDragged = false;
        }, 40);
      }
    }
  );
}
